import React, { useId } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Input } from 'vendor/heroui';
import { Modal } from 'components/HeroSemantic';
import passkeyButtonClassName, { passkeyPrimaryButtonClassName } from 'components/Auth/passkeyButtonClassName';

// OWN-219: shared by every "add a passkey" entry point (the Security & Login
// page, the login-time nudge, the mandatory hard-block screen, and account
// recovery) so naming a passkey behaves identically everywhere, not just on
// the Security & Login page. This is purely our own app-side label - WebAuthn
// gives the relying party no way to set the name the browser/OS itself shows
// for the credential, only the account (see owner-api's user.displayName).
export const MAX_DEVICE_NAME_LENGTH = 80;

const AddPasskeyModal = ({ open, onCancel, onContinue, isBusy }) => {
  const { formatMessage } = useIntl();
  const inputId = useId();

  const handleContinue = () => {
    const deviceName = document.getElementById(inputId)?.value.trim();
    onContinue(deviceName || undefined);
  };

  return (
    // No onClose - a stray backdrop click (easy to trigger on mobile) must
    // not silently dismiss this. When opened from the FTR-3 mandatory
    // hard-block, dismissing drops the owner back into that modal, which
    // reads as a confusing loop rather than an intentional cancel. Only the
    // explicit Cancel button below may invoke onCancel - same rule
    // RecoveryCodesModal already follows for its own one-time content.
    <Modal open={open}>
      <Modal.Header>
        <FormattedMessage id="page.passkeys.addModal.title" defaultMessage="Add a passkey" />
      </Modal.Header>
      <Modal.Content>
        <p className="mb-4 text-sm text-[var(--owner-text)]">
          <FormattedMessage
            id="page.passkeys.addModal.description"
            defaultMessage="Optionally, name this passkey so you can recognize it later - e.g. 'Home iMac'. Leave it blank and we'll name it for you."
          />
        </p>
        <Input
          fullWidth
          id={inputId}
          name="deviceName"
          type="text"
          maxLength={MAX_DEVICE_NAME_LENGTH}
          placeholder={formatMessage({ id: 'page.passkeys.addModal.placeholder', defaultMessage: 'Device name (optional)' })}
        />
      </Modal.Content>
      <Modal.Actions>
        <Button type="button" className={passkeyButtonClassName} onPress={onCancel}>
          <FormattedMessage id="general.cancel" defaultMessage="Cancel" />
        </Button>
        <Button type="button" isDisabled={isBusy} className={passkeyPrimaryButtonClassName} onPress={handleContinue}>
          <FormattedMessage id="general.continue" defaultMessage="Continue" />
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

AddPasskeyModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired,
  isBusy: PropTypes.bool,
};

AddPasskeyModal.defaultProps = {
  isBusy: false,
};

export default AddPasskeyModal;
